<script setup lang="ts">
import {
    ref, onMounted, type Ref,
    onUnmounted,
} from 'vue';
import { useQuasar } from 'quasar';
import { useSchoolStore } from '@/stores/school';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import type { KnowledgeTestAgreementResponse } from '@/types/Response/FlightSchoolResponse';
import StudentCard from '@/components/Student/StudentCard.vue';
import StudentRecordTabs from '@/components/Student/StudentRecordTabs.vue';
import BreadCrumbs from '@/components/Utilities/BreadCrumbs.vue';
import type BreadCrumb from '@/types/BreadCrumb';
import CoursePerformanceRepository from '@/repositories/CoursePerformanceRepository';
import type {
    StudentRatingTimeInformation,
    StudentRatingPerformance,
} from '@/types/Performance';
import { usePerformanceStore } from '@/stores/performance';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import { notifyAndHandleNoAccess } from '@/functions/routeHelpers';
import router from '@/router';

const props = defineProps<{
    cusId: string,
}>();

const schoolStore = useSchoolStore();
const tab = ref<string>('Student Information');
const currentStudent = ref<FlightSchoolCourseStudent|null>(null);
const studentTiming: Ref<StudentRatingTimeInformation|null> = ref(null);
const studentPerformance: Ref<StudentRatingPerformance|null> = ref(null);
const averageQuizScore: Ref<number|null> = ref(null);

const kta = ref<KnowledgeTestAgreementResponse>();
const $q = useQuasar();

const updateStudentTiming = async (
    school: FlightSchool|null,
    course: FlightSchoolCourse|null,
    student: FlightSchoolCourseStudent|null,
) => {
    if (!school || !course || !student) {
        return;
    }

    studentTiming.value = await CoursePerformanceRepository.getStudentRatingTiming(school, course, student);
};

onMounted(async () => {
    if (schoolStore.school !== null && schoolStore.course !== null) {
        $q.loading.show();

        await schoolStore.setStudents();

        const student = schoolStore.findStudent(parseInt(props.cusId, 10));
        if (!student) {
            $q.loading.hide();

            const route = await notifyAndHandleNoAccess();
            router.push(route);
            return;
        }

        currentStudent.value = student;

        kta.value = await FlightSchoolRepository.getStudentKnowledgeTestAgreement(
            schoolStore.school,
            schoolStore.course,
            currentStudent.value,
        );

        studentPerformance.value = await CoursePerformanceRepository.getStudentRatingPerformance(
            schoolStore.school,
            schoolStore.course,
            currentStudent.value,
        );

        await updateStudentTiming(schoolStore.school, schoolStore.course, currentStudent.value);
        averageQuizScore.value = studentPerformance.value?.averageScore ?? null;

        const performanceStore = usePerformanceStore();
        performanceStore.setPerformance(studentPerformance.value);

        $q.loading.hide();
    }
});

onUnmounted(() => {
    $q.loading.hide();
});

const getBreadcrumb = (): BreadCrumb => {
    const breadcrumbPath = router.options.history.state.back;
    const route = router.resolve({ path: `${breadcrumbPath}` });
    const breadcrumbName = route.name?.toString();

    if (breadcrumbName && typeof route.meta.displayName === 'string') {
        return { to: { name: breadcrumbName, params: route.params }, label: route.meta.displayName };
    }

    return { to: { name: 'classOverview' }, label: 'Class Overview' };
};
</script>
<template>
    <div
        v-if="schoolStore.school === null || schoolStore.course === null"
        class="row"
    >
        <q-banner
            class="text-white bg-red"
        >
            You must select a school and course.
        </q-banner>
    </div>
    <div v-else>
        <div
            v-if="currentStudent && !$q.loading.isActive"
            class="row"
        >
            <BreadCrumbs
                :bread-crumbs="[getBreadcrumb()]"
                current-page="Student Record"
            />
            <div class="col-12">
                <div class="row">
                    <div class="col-12">
                        <h4
                            id="student-record-head"
                            class="fsum-generic-header text-center"
                        >
                            Student Record
                        </h4>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12 student-record-card">
                        <StudentCard
                            :student="currentStudent"
                            :school="schoolStore.school"
                            :course="schoolStore.course"
                            :student-timing="studentTiming"
                            :average-quiz-score="averageQuizScore"
                        />
                    </div>
                </div>
                <StudentRecordTabs
                    :student="currentStudent"
                    :school="schoolStore.school"
                    :course="schoolStore.course"
                    :kta="kta"
                    :tab="tab"
                    :student-timing="studentTiming"
                    :student-performance="studentPerformance"
                    @update:time="updateStudentTiming(schoolStore.school, schoolStore.course, currentStudent)"
                />
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
.student-record-card {
    padding: 0 48px 48px 48px;

    @media screen and (max-width: $breakpoint-md-min) {
        padding: 0 16px 48px;
    }

    @media screen and (max-width: $breakpoint-sm-min) {
        padding: 0;
    }
}
</style>
